import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faBookmark,
  faCheckCircle,
  faCheckSquare,
  faCircle,
  faMinusSquare,
  faQuestionCircle,
  faSquare,
} from '@fortawesome/free-regular-svg-icons';
import {
  faBars,
  faBookmark as faBookmarkSolid,
  faCaretDown,
  faCaretUp,
  faCheck,
  faChevronCircleDown,
  faChevronCircleUp,
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faDollarSign,
  faEllipsisH,
  faFileInvoice,
  faFilter,
  faLevelUpAlt,
  faMinus,
  faPlus,
  faShoppingCart,
  faSpinner,
  faTh,
  faThList,
  faUndo,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Angulartics2Module } from 'angulartics2';
import { AtatusErrorHandler, LmsApiModule } from 'lms-api'
import { AppSettings, LoginExceptionRedirectFactory, LoginExceptionRedirectToken, PacApiModule } from 'pac-api';

import { SetBackgroundColorDirective } from './_directives/set-background-color.directive';
import { SetBorderColorDirective } from './_directives/set-border-color.directive';
import { SetTextColorDirective } from './_directives/set-text-color.directive';
import { DeferLoadDirective } from './_directives/defer-load.directive';
import { SharedModule } from './_modules/shared/shared.module';
import { CourseFilterPipe } from './_pipes/course-filter.pipe';
import { CourseListSortPipe } from './_pipes/course-list-sort.pipe';
import { CourseStatusFilterPipe } from './_pipes/course-status-filter.pipe';
import { DateFormatPipe } from './_pipes/date-format.pipe';
import { ExcludeArchivedCoursesPipe } from './_pipes/exclude-archived-courses.pipe';
import { LicenseFilterPipe } from './_pipes/license-filter.pipe';
import { bundleListPipe } from './_pipes/purchased-bundle.pipe';
import { AddBundleToCartComponent } from './add-bundle-to-cart/add-bundle-to-cart.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BundleListComponent } from './bundle-list/bundle-list.component';
import { BundleViewComponent } from './bundle-view/bundle-view.component';
import { CartDiscountUpsellComponent } from './cart-discount-upsell/cart-discount-upsell.component';
import { CheckoutPaymentModalComponent } from './checkout-payment-modal/checkout-payment-modal.component';
import { CheckoutComponent } from './checkout/checkout.component';
import { CourseCarouselComponent } from './course-carousel/course-carousel.component';
import { CourseListFilterComponent } from './course-list-filter/course-list-filter.component';
import { CourseListComponent } from './course-list/course-list.component';
import { CourseListviewGridComponent } from './course-listview-grid/course-listview-grid.component';
import { CourseListviewListComponent } from './course-listview-list/course-listview-list.component';
import { CourseSlideshowComponent } from './course-slideshow/course-slideshow.component';
import { CourseViewGridComponent } from './course-view-grid/course-view-grid.component';
import { CourseViewListComponent } from './course-view-list/course-view-list.component';
import { CoursesAllComponent } from './courses-all/courses-all.component';
import { DashboardSwitcherComponent } from './dashboard-switcher/dashboard-switcher.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { FavoriteCourseWidgetComponent } from './favorite-course-widget/favorite-course-widget.component';
import { HeaderLoginformComponent } from './header-loginform/header-loginform.component';
import { HeaderUserinfoComponent } from './header-userinfo/header-userinfo.component';
import { HeaderComponent } from './header/header.component';
import { LandingAnonymousComponent } from './landing-anonymous/landing-anonymous.component';
import { LandingCeSocialsComponent } from './landing-ce-socials/landing-ce-socials.component';
import { LandingCECenterComponent } from './landing-cecenter/landing-cecenter.component';
import { LandingCppComponent } from './landing-cpp/landing-cpp.component';
import { LandingHeaderComponent } from './landing-header/landing-header.component';
import { LandingNonmemberComponent } from './landing-nonmember/landing-nonmember.component';
import { LandingStudentComponent } from './landing-student/landing-student.component';
import { LicenseUpdateComponent } from './license-update/license-update.component';
import { LoginComponent } from './login/login.component';
import { MarketingLandingComponent } from './marketing-landing/marketing-landing.component';
import { MessageDisplayComponent } from './message-display/message-display.component';
import { MyAccountComponent } from './my-account/my-account.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { SalesBoxComponent } from './sales-box/sales-box.component';
import { ShoppingCartDisplayComponent } from './shopping-cart-display/shopping-cart-display.component';
import { StyleGuideComponent } from './style-guide/style-guide.component';
import { ThumbnailHighlightComponent } from './thumbnail-highlight/thumbnail-highlight.component';
import { TranscriptComponent } from './transcript/transcript.component';
import { ViewOptionsComponent } from './view-options/view-options.component';
import { CarouselModule } from 'ngx-carousel-ease';

let providersArray = [
  {
    provide: LoginExceptionRedirectToken,
    useFactory: (settings: AppSettings) => LoginExceptionRedirectFactory(settings.appUrl),
    deps: [AppSettings]
  },
  { provide: ErrorHandler, useClass: AtatusErrorHandler }
];
if (location.hostname == 'localhost') {
  providersArray.pop();
}

@NgModule({
  declarations: [
    AppComponent,
    CourseListComponent,
    DashboardComponent,
    HeaderComponent,
    HeaderLoginformComponent,
    HeaderUserinfoComponent,
    LoginComponent,
    CourseListFilterComponent,
    CoursesAllComponent,
    CourseListviewGridComponent,
    CourseListviewListComponent,
    FavoriteCourseWidgetComponent,
    CourseListSortPipe,
    CourseSlideshowComponent,
    CourseViewListComponent,
    CourseViewGridComponent,
    BundleListComponent,
    BundleViewComponent,
    StyleGuideComponent,
    TranscriptComponent,
    CourseStatusFilterPipe,
    MessageDisplayComponent,
    ViewOptionsComponent,
    ExcludeArchivedCoursesPipe,
    CourseFilterPipe,
    LandingAnonymousComponent,
    LandingCppComponent,
    LandingStudentComponent,
    LandingNonmemberComponent,
    LandingHeaderComponent,
    CourseCarouselComponent,
    LandingCECenterComponent,
    ThumbnailHighlightComponent,
    SetBackgroundColorDirective,
    SetBorderColorDirective,
    SetTextColorDirective,
    DeferLoadDirective,
    NotFoundComponent,
    DateFormatPipe,
    DashboardSwitcherComponent,
    ShoppingCartDisplayComponent,
    CartDiscountUpsellComponent,
    CheckoutPaymentModalComponent,
    CheckoutComponent,
    AddBundleToCartComponent,
    MarketingLandingComponent,
    MyAccountComponent,
    bundleListPipe,
    LicenseUpdateComponent,
    LicenseFilterPipe,
    SalesBoxComponent,
    LandingCeSocialsComponent
  ],
  bootstrap: [AppComponent],
  imports: [BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FontAwesomeModule,
    NgbModule,
    FormsModule,
    ReactiveFormsModule,
    Angulartics2Module.forRoot(),
    CarouselModule,
    PacApiModule.forRoot(),
    LmsApiModule.forRoot(),
    SharedModule],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: LoginExceptionRedirectToken,
      useFactory: (settings: AppSettings) => LoginExceptionRedirectFactory(settings.appUrl),
      deps: [AppSettings]
    }]
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIcons(faMinus, faDollarSign, faShoppingCart, faSquare, faCheckSquare, faMinusSquare, faCircle, faCheckCircle, faCheck, faChevronCircleDown, faChevronCircleUp, faTh, faThList, faBookmark, faBookmarkSolid, faFileInvoice, faUser, faBars, faFilter, faCaretDown, faCaretUp, faUndo, faSpinner, faChevronRight, faChevronUp, faChevronDown, faCheck, faPlus, faQuestionCircle, faLevelUpAlt, faEllipsisH);
  }
}
